/**
 * JobCard Component
 * Clean, modern card for grid view with minimal clutter
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { Job, JobStatus } from "../../types";
import { Card } from "../common";
import { formatDate, formatJobType } from "../../utils";
import { cn } from "../../utils";
import { ROUTES } from "../../services/routes";
import { downloadFile } from "../../utils";
import {
  Users,
  Car,
  ParkingCircle,
  ArrowLeftRight,
  CircleDot,
  Utensils,
  BedDouble,
  Building2,
  PawPrint,
  BarChart3,
  Video,
  HardHat,
  Box,
  MonitorPlay,
  Eye,
  Download,
  Trash2,
  CheckCircle2,
  Clock,
  Loader2,
  XCircle,
} from "lucide-react";

// Job type to icon mapping
const JOB_TYPE_ICONS: Record<string, React.ReactNode> = {
  people_counting: <Users className="w-5 h-5" />,
  number_plate_detection: <Car className="w-5 h-5" />,
  parking_analysis: <ParkingCircle className="w-5 h-5" />,
  in_out_counting: <ArrowLeftRight className="w-5 h-5" />,
  pothole_detection: <CircleDot className="w-5 h-5" />,
  food_waste_estimation: <Utensils className="w-5 h-5" />,
  room_readiness: <BedDouble className="w-5 h-5" />,
  lobby_crowd_detection: <Building2 className="w-5 h-5" />,
  wildlife_detection: <PawPrint className="w-5 h-5" />,
  ppe_detection: <HardHat className="w-5 h-5" />,
  damage_detection_3d: <Box className="w-5 h-5" />,
  multi_camera: <MonitorPlay className="w-5 h-5" />,
};

// Status config
const STATUS_CONFIG = {
  [JobStatus.COMPLETED]: {
    icon: <CheckCircle2 className="w-4 h-4" />,
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
  },
  [JobStatus.PROCESSING]: {
    icon: <Loader2 className="w-4 h-4 animate-spin" />,
    color: "text-primary-400",
    bg: "bg-primary-500/10",
    border: "border-primary-500/30",
  },
  [JobStatus.PENDING]: {
    icon: <Clock className="w-4 h-4" />,
    color: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
  },
  [JobStatus.FAILED]: {
    icon: <XCircle className="w-4 h-4" />,
    color: "text-red-400",
    bg: "bg-red-500/10",
    border: "border-red-500/30",
  },
};

interface JobCardProps {
  job: Job;
  onDelete?: (jobId: string) => void;
  onView?: (jobId: string) => void;
  className?: string;
}

export const JobCard: React.FC<JobCardProps> = ({
  job,
  onDelete,
  onView,
  className,
}) => {
  const navigate = useNavigate();
  const icon = JOB_TYPE_ICONS[job.job_type] || <BarChart3 className="w-5 h-5" />;
  const statusConfig = STATUS_CONFIG[job.status] || STATUS_CONFIG[JobStatus.PENDING];

  const handleView = () => {
    if (onView) {
      onView(job.id);
    } else {
      navigate(ROUTES.JOB_DETAILS(job.id));
    }
  };

  const handleDownload = () => {
    if (!job.annotated_output_url) return;
    try {
      const isVideo = job.input_file_path?.match(/\.(mp4|avi|mov)$/i);
      downloadFile(
        job.annotated_output_url,
        `job_${job.job_number}_annotated.${isVideo ? "mp4" : "png"}`
      );
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(job.id);
    }
  };

  return (
    <Card 
      variant="glass" 
      padding="none" 
      hover 
      className={cn("overflow-hidden group", className)}
    >
      {/* Header with status indicator */}
      <div className={cn(
        "px-4 py-3 border-b border-slate-700/50 flex items-center justify-between",
        statusConfig.bg
      )}>
        <div className="flex items-center gap-2">
          <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", statusConfig.bg, "border", statusConfig.border)}>
            <span className={statusConfig.color}>{icon}</span>
          </div>
          <div>
            <p className="text-white font-medium text-sm">{formatJobType(job.job_type)}</p>
            {job.is_live_demo && (
              <span className="inline-flex items-center gap-1 text-xs text-accent-400">
                <Video className="w-3 h-3" /> Live Demo
              </span>
            )}
          </div>
        </div>
        <div className={cn(
          "inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium",
          statusConfig.bg, statusConfig.color, "border", statusConfig.border
        )}>
          {statusConfig.icon}
          <span className="capitalize">{job.status}</span>
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <p className="text-slate-400 text-xs mb-3 font-mono truncate" title={String(job.job_number)}>
          #{String(job.job_number).slice(-12)}
        </p>

        <div className="flex items-center justify-between text-xs mb-4">
          <span className="text-slate-500">
            {formatDate(job.submitted_at, "MMM dd, HH:mm")}
          </span>
          {job.completed_at && job.status === JobStatus.COMPLETED && (
            <span className="text-emerald-400/60">
              Done {formatDate(job.completed_at, "HH:mm")}
            </span>
          )}
        </div>

        {/* Error message */}
        {job.error_message && (
          <div className="text-xs p-2 mb-3 bg-red-500/10 rounded-lg border border-red-500/20 truncate">
            <span className="text-red-400">{job.error_message}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleView}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-medium border border-slate-600/50 transition-all"
          >
            <Eye className="w-3.5 h-3.5" />
            View
          </button>

          {job.status === JobStatus.COMPLETED && (
            <button
              onClick={handleDownload}
              className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-400 hover:text-primary-300 border border-slate-600/50 hover:border-primary-500/30 transition-all"
              title="Download"
            >
              <Download className="w-3.5 h-3.5" />
            </button>
          )}

          {onDelete && (
            <button
              onClick={handleDelete}
              className="p-2 rounded-lg bg-slate-700/50 hover:bg-red-500/10 text-slate-400 hover:text-red-400 border border-slate-600/50 hover:border-red-500/30 transition-all"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </Card>
  );
};
